import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PlaceImage } from "~/lib/api";
import { Button } from "~/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "~/components/ui/carousel";
import { Icon } from "~/components/ui/icons/icon";
import { ScrollingDots } from "~/components/ui/scrolling-dots";

interface ImageLightboxProps {
  images: PlaceImage[];
  placeName: string;
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

/** Fullscreen lightbox for viewing place images */
export function ImageLightbox({ images, placeName, initialIndex = 0, open, onClose }: ImageLightboxProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(initialIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  // Scroll to initial index when opening
  useEffect(() => {
    if (open && api) {
      api.scrollTo(initialIndex, true);
      setCurrent(initialIndex);
    }
  }, [open, api, initialIndex]);

  // Focus management: capture trigger, focus container, restore on close
  useEffect(() => {
    if (open) {
      // Store the element that triggered the lightbox
      triggerRef.current = document.activeElement;
      // Focus the container
      containerRef.current?.focus();
    } else if (triggerRef.current) {
      // Restore focus to trigger when closing
      (triggerRef.current as HTMLElement).focus?.();
      triggerRef.current = null;
    }
  }, [open]);

  // Focus trap: keep focus within lightbox
  useEffect(() => {
    if (!open) return;

    const handleFocusTrap = (e: FocusEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        e.preventDefault();
        containerRef.current.focus();
      }
    };

    document.addEventListener("focusin", handleFocusTrap);
    return () => document.removeEventListener("focusin", handleFocusTrap);
  }, [open]);

  // Subscribe to slide changes
  useEffect(() => {
    if (!api) return;
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        api?.scrollPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        api?.scrollNext();
      }
    },
    [api, onClose],
  );

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open || images.length === 0) return null;

  const showCounter = images.length > 1;

  return createPortal(
    <div
      ref={containerRef}
      tabIndex={-1}
      className="fixed inset-0 z-modal bg-background/95 backdrop-blur-sm flex flex-col outline-none animate-in fade-in zoom-in-95 duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo gallery for ${placeName}`}
      onKeyDown={handleKeyDown}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <Icon i="close" className="size-6" />
          <span className="sr-only">Close gallery</span>
        </Button>
        {showCounter && <span className="text-muted-foreground text-sm">{`${current + 1} / ${images.length}`}</span>}
      </div>

      {/* Carousel */}
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <Carousel
          setApi={setApi}
          opts={{ loop: true, startIndex: initialIndex }}
          className="w-full h-full [&>div]:h-full"
        >
          <CarouselContent className="ml-0 h-full">
            {images.map((image, index) => (
              <CarouselItem key={image.id} className="pl-0 h-full flex items-center justify-center">
                <img
                  src={image.large}
                  srcSet={image.srcset}
                  sizes="100vw"
                  alt={`Photo ${index + 1} of ${placeName}`}
                  className="max-h-full max-w-full object-contain"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {showCounter && (
        <div className="flex justify-center p-4">
          <ScrollingDots
            total={images.length}
            current={current}
            onSelect={(i) => api?.scrollTo(i)}
            activeClass="bg-foreground"
            inactiveClass="bg-foreground/40"
          />
        </div>
      )}
    </div>,
    document.body,
  );
}
